<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { groupsApi } from '@/api/groups';

interface Props {
  modelValue: boolean;
  memberName: string;
  initialGroupIds: number[];
  loading?: boolean;
  errorMessage?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errorMessage: null,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', groupIds: number[]): void;
  (e: 'clearError'): void;
}>();

const selectedIds = ref<number[]>([]);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const { data: groups } = useQuery({
  queryKey: ['groups', 'forFilters'],
  queryFn: () => groupsApi.indexForFilters(),
  initialData: [],
});

watch(() => props.modelValue, (open) => {
  if (open) selectedIds.value = [...props.initialGroupIds];
});

// A manager can't leave someone without a group while the company has groups.
const groupRequired = computed(() => (groups.value ?? []).length > 0);
const isEmptySelection = computed(() => selectedIds.value.length === 0);
const blocksSave = computed(() => groupRequired.value && isEmptySelection.value);
const showEmptyWarning = computed(() => blocksSave.value && !props.errorMessage);

const title = computed(() =>
  `Editar grupos · ${props.memberName} (${props.initialGroupIds.length})`);

function onSave() {
  if (blocksSave.value) return;
  emit('clearError');
  emit('save', selectedIds.value);
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    max-width="500"
  >
    <v-card>
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedIds"
          :items="groups ?? []"
          item-title="name"
          item-value="id"
          label="Grupos"
          variant="outlined"
          multiple
          chips
          closable-chips
          hide-details
        />
        <v-alert
          v-if="showEmptyWarning"
          density="compact"
          type="warning"
          variant="tonal"
          class="mt-4"
        >
          Selecciona al menos un grupo.
        </v-alert>
        <v-alert
          v-if="errorMessage"
          density="compact"
          type="error"
          variant="tonal"
          class="mt-4"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="isOpen = false">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="blocksSave"
          @click="onSave"
        >
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
